import { BfiApi, IApiResponse } from './bfiApi';
import { ISearchCriteria } from './common';
import { LoanType, WatchlistCategoryType, WatchlistCureType, WatchlistSummaryType } from '@apply/constants';

export interface IWatchlistIssue {
    loanId: number;
    watchlistId: number;
    categoryTypeId: WatchlistCategoryType;
    otherCategoryName: string;
    curedDate?: Date | null;
    CureTypeId?: WatchlistCureType | null;
    OtherCureName: string | null;
    isOutstanding: boolean;
}

export interface IWatchlistLoanUpdate {
    loanId: number;
    watchlistId: number;
    notes: string;
    issues: IWatchlistIssue[];
}

export interface IWatchlistLoanPropertyUpdate {
    propertyId: number;
    watchlistId: number;
    notes: string;
    issues: IWatchlistIssue[];
}

export interface IWatchlistLoanDetail {
    entityName: string;
    bfiNumber: string;
    noteDate: Date;
    maturityDate: Date;
    totalTerm: number;
    pocName: string;
    issues: IWatchlistIssue[];
}

export interface IWatchlistSearchRequest extends ISearchCriteria {
    watchlidtId: number;
    loanTypeIds: LoanType[];
}

export interface IItemGroupItem {
    loanId: number;
    propertyId: number;
    sortOrder: number;
}

export interface IItemGroupUpdate {
    watchlistId: number;
    items: IItemGroupItem[];
    notes: string;
}

export interface IGroupRemoval {
    id: number;
    watchlistId: number;
}

export class WatchlistApi extends BfiApi {
    getWatchlists(): Promise<IApiResponse[]> {
        return super.get(`/watchlists`);
    }

    getWatchlistById(id: number): Promise<IApiResponse[]> {
        return super.get(`/watchlists/${id}`);
    }

    searchWatchlist(model: IWatchlistSearchRequest): Promise<IApiResponse[]> {
        return super.post(`/watchlists/items/search`, model);
    }

    getWatchlistLoans(watchlistId: number, criteria: ISearchCriteria): Promise<IApiResponse[]> {
        return super.post(`/watchlists/${watchlistId}/loans/list`, criteria);
    }

    getWatchlistLoanDetails(watchlistId: number, loanId: number): Promise<IApiResponse[]> {
        return super.get(`/watchlists/${watchlistId}/loans/${loanId}`);
    }

    getWatchlistPropertyDetails(watchlistId: number, propertyId: number): Promise<IApiResponse[]> {
        return super.get(`/watchlists/${watchlistId}/properties/${propertyId}`);
    }

    updateLoan(model: IWatchlistLoanUpdate): Promise<IApiResponse> {
        return super.post('/watchlists/loans', model);
    }

    getWatchlistLoanIssues(watchlistId: number, loanId: number): Promise<IApiResponse[]> {
        return super.get(`/watchlists/${watchlistId}/loans/${loanId}/issues`);
    }

    getWatchlistPropertyIssues(watchlistId: number, propertyId: number): Promise<IApiResponse[]> {
        return super.get(`/watchlists/${watchlistId}/properties/${propertyId}/issues`);
    }

    updateProperty(model: IWatchlistLoanPropertyUpdate): Promise<IApiResponse> {
        return super.post('/watchlists/properties', model);
    }

    getGradeHistory(loanId: number): Promise<IApiResponse> {
        return super.get(`/watchlists/loans/${loanId}/grades/history`);
    }

    getMaturityHistory(loanId: number): Promise<IApiResponse> {
        return super.get(`/watchlists/loans/${loanId}/maturity/history`);
    }

    getDrawFundingHistory(loanId: number): Promise<IApiResponse> {
        return super.get(`/watchlists/loans/${loanId}/drawfunding/history`);
    }

    getSummaryBySummaryType(summaryTypeId: WatchlistSummaryType): Promise<IApiResponse> {
        return super.get(`/watchlists/summaries/${summaryTypeId}`);
    }

    getEntityWatchlistItems(watchlistId: number, entityId: number): Promise<IApiResponse> {
        return super.get(`/watchlists/${watchlistId}/entities/${entityId}/items`);
    }

    getEntitySummaryById(entityId: number): Promise<IApiResponse> {
        return super.get(`/watchlists/entities/${entityId}`);
    }

    getLoanNotes(loanId: number): Promise<IApiResponse> {
        return super.get(`/watchlists/loans/${loanId}/notes`);
    }

    getPropertyNotes(propertyId: number): Promise<IApiResponse> {
        return super.get(`/watchlists/properties/${propertyId}/notes`);
    }

    getLiquidityHistory(loanId: number): Promise<IApiResponse> {
        return super.get(`/watchlists/loans/${loanId}/liquidity/history`);
    }
    getAppraisalsHistoryByProperty(propertyId: number): Promise<IApiResponse> {
        return super.get(`/watchlists/properties/${propertyId}/appraisals/history`);
    }
    getAppraisalsHistoryByLoan(loanId: number): Promise<IApiResponse> {
        return super.get(`/watchlists/loans/${loanId}/appraisals/history`);
    }
    getLoansNotInWatchlist(watchlistId: number, summaryTypeId: WatchlistSummaryType, bfiNumber: string): Promise<IApiResponse> {
        return super.get(`/watchlists/${watchlistId}/loansbytype/${summaryTypeId}/notinwatchlist?bfinumber=${bfiNumber}`);
    }
    getItemGroup(watchlistId: number, groupId: number, summaryTypeId: WatchlistSummaryType): Promise<IApiResponse> {
        return super.get(`/watchlists/${watchlistId}/groups/${groupId ?? 0}?summaryTypeId=${summaryTypeId}`);
    }
    updateItemGroup(group: IItemGroupUpdate): Promise<IApiResponse> {
        return super.post(`/watchlists/groups`, group);
    }
    deleteGroupOnThisAndFutureWatchlists(group: IGroupRemoval): Promise<IApiResponse> {
        return super.post(`/watchlists/groups/delete`, group);
    }

    getWatchListSummary(summaryTypeId: WatchlistSummaryType): Promise<IApiResponse> {
        return super.get(`/watchlists/summary/${summaryTypeId}`);
    }
}
